import { useFrame, useThree } from '@react-three/fiber';
import { Vector3 } from 'three';
import { useShallow } from 'zustand/shallow';

import {
    raycastPlane,
    dragAnchorPositionRef
} from 'app/modules/wallforming/components/scenes/objects/articles/utils/dragUtils';
import { usePlacementStore } from 'app/modules/wallforming/components/state-management/placement';

const cameraDir = new Vector3();
const refPoint = new Vector3();

/**
 * An invisible plane mesh that is always orthogonal to the camera.
 *
 * - During drag: positioned at `dragAnchorPositionRef` so the plane depth
 *   matches the article being dragged.
 * - Otherwise: positioned at `pointerPositionRef` (last hover hit) so placement
 *   raycasting resolves to a sensible scene depth.
 * - Falls back to a fixed distance in front of the camera when neither ref
 *   is available.
 * - Updates the idle hover plane every frame. Active and pending placement
 *   sessions own their plane until they finish.
 */
export const useRaycastPlane = () => {
    const isDraggingActive = usePlacementStore(useShallow((state) => state.isDraggingActive));
    const camera = useThree((state) => state.camera);

    useFrame(() => {
        const state = usePlacementStore.getState();
        if (isDraggingActive() || state.isAutoDragActiveRef.current || state.isDragNewArticleStartRef.current) {
            return;
        }

        // Always orthogonal to the camera
        camera.getWorldDirection(cameraDir);

        // Choose the depth anchor: drag target → last hover point → fixed fallback.
        const depthAnchor = dragAnchorPositionRef.current ?? usePlacementStore.getState().pointerPositionRef.current;
        if (depthAnchor) {
            refPoint.copy(depthAnchor);
        } else {
            camera.getWorldPosition(refPoint);
            refPoint.addScaledVector(cameraDir, 10);
        }

        raycastPlane.setFromNormalAndCoplanarPoint(cameraDir, refPoint);
    });
};
